import React, { useEffect, useState } from "react"
import { BanknotesIcon, CreditCardIcon } from "@heroicons/react/24/outline"
import Value, { setValue } from "aldoo-ra/Value"
import { PAY_AT_SHOP_KEY } from "./payment"
import { showErrorToast } from "aldoo-ra/Toast"
import { CHECKOUT_ALTERNATIVE_KEY } from "aldoo-ra/Store/elements/checkout-form"
import useT from "../hooks/useT"
import { useCartPaymentMethods } from "../ui/cart/paymentMethods"
import { CheckoutCashOnDelivery, getDeviceID } from "../storeApi"
import PaymentLogos from "./PaymentLogos"

const ERRORS = {
  cash_on_delivery_unavailable: "Cash on delivery is not available for this cart",
  already_paid: "This order is already paid",
  already_placed: "This order is already placed",
}

/**
 * The payment step's choice (fragment "checkout-form-header", above the card form): cash on
 * delivery (наложен платеж), first and chosen to begin with, or pay by card now — the first offered only when the cart can be paid so (every
 * seller takes it, everything travels: cart_payment_methods). Cash on delivery hides the card form
 * and makes the button "Place the order" (checkout-form's CHECKOUT_ALTERNATIVE_KEY): the order is
 * placed at once (store {checkout_cod}) and the thank-you says so, with nothing to wait for.
 */
export default function PaymentChoice({ store }) {
  const t = useT()
  const methods = useCartPaymentMethods(store.cart || [])
  // cash on delivery first and chosen, when the cart can be paid so; the card under it
  const [choice, setChoice] = useState("cod")
  // collected at the seller's shop: paid there, not to a courier
  const [atShop] = Value(PAY_AT_SHOP_KEY, false)
  const offered = methods.cashOnDelivery
  // a seller who takes no payment online: cash on delivery is the way
  const cashOnly = offered && !methods.online
  const current = cashOnly ? "cod" : offered ? choice : "card"

  useEffect(() => {
    if (current !== "cod") {
      setValue(CHECKOUT_ALTERNATIVE_KEY, null)
      return
    }
    setValue(CHECKOUT_ALTERNATIVE_KEY, {
      id: "cash_on_delivery",
      continueText: t`Place the order`,
      submit: async () => {
        const paymentIntentId = store.paymentInfo?.paymentIntentId
        if (!paymentIntentId) return showErrorToast(t`The order could not be placed`, t`Please try again`)
        const response = await CheckoutCashOnDelivery(paymentIntentId, await getDeviceID()).catch((error) => ({ error: error?.message || "request_failed" }))
        if (!response?.placed) return showErrorToast(t`The order could not be placed`, t(ERRORS[response?.error] || "Please try again"))
        // the thank-you: an order placed, its payment on delivery — nothing to wait for
        store.onPaymentSuccess({ receipt: { cashOnDelivery: true, atShop: !!atShop, paymentIntentId: null, orders: response.orders || [] } })
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, store.paymentInfo?.paymentIntentId, atShop])
  // the step left (or the checkout closed): no alternative left set for the next checkout
  useEffect(() => () => setValue(CHECKOUT_ALTERNATIVE_KEY, null), [])

  if (!offered) return null
  const option = (id, icon, title, note, extra) => (
    <label
      className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${current === id ? "border-primary ring-1 ring-primary bg-paper" : "border-bordered hover:border-text_secondary"}`}
      data-testid={`payment-choice-${id}`}
      data-on={current === id ? "on" : "off"}
    >
      <input type="radio" name="payment-choice" className="sr-only" checked={current === id} onChange={() => setChoice(id)} disabled={cashOnly} />
      <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${current === id ? "border-primary" : "border-bordered"}`}>
        {current === id && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
      </span>
      <span className="flex-1 min-w-0 flex flex-col gap-1.5">
        <span className="flex items-center gap-2 font-semibold text-[15px]">
          {icon}
          {title}
        </span>
        {note && <span className="text-[13px] text-text_secondary">{note}</span>}
        {extra}
      </span>
    </label>
  )
  return (
    <div className="mb-5 flex flex-col gap-2.5" data-testid="payment-choice">
      {option("cod", <BanknotesIcon className="w-5 h-5 text-success" />, atShop ? t`Pay at the shop` : t`Cash on delivery`, atShop ? t`Pay on the spot at the shop` : t`You pay the courier when the parcel arrives`)}
      {methods.online && option("card", <CreditCardIcon className="w-5 h-5" />, t`Pay by card`, null, <PaymentLogos online cashOnDelivery={false} />)}
    </div>
  )
}
